<template>
  <div>
    <Loader :messageSelector="messageSelector" :overlay="overlay" />

    <v-form ref="form" v-model="valid">
      <fieldset>
        <legend class="text-h6 text-sm-h5">Autorisation(s) de pratique</legend>
        <RessourceInfoBulle code="PI-AP-1" />
        <v-simple-table>
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-left"></th>
                <th class="text-left"></th>
                <th class="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in listeAutorisationsDePratique.filter((a) => a.demandeInscription && a.demandeInscription.length > 0)"
                :key="item.name"
                :class="{ 'red lighten-3': item.statut != null && item.statut.id === $catalogues.statutAutorisationDePatique.retire }"
              >
                <td class="pt-4">
                  <v-autocomplete
                    v-if="isNotRetire(item)"
                    v-model="item.categorieAutorisation"
                    :item-text="itemText"
                    :item-value="itemValue"
                    :items="listeCategoriesAutorisationsFilter"
                    :rules="rules.categorieAutorisation"
                    disabled
                    label="Catégorie d'autorisation"
                    required
                    return-object
                  ></v-autocomplete>
                </td>
                <td class="pt-4">
                  <v-autocomplete
                    v-if="isNotRetire(item)"
                    v-model="item.autorisation"
                    :item-text="itemText"
                    :item-value="itemValue"
                    :items="listeFilteredAutorisations(item.categorieAutorisation.id)"
                    :rules="rules.autorisation"
                    disabled
                    label="Autorisation"
                    required
                    return-object
                  ></v-autocomplete>
                </td>
                <td class="text-right pt-4">
                  <v-btn v-if="item.statut != null && item.statut.id === $catalogues.statutAutorisationDePatique.retire" color="primary" icon @click="addRow(item)">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </fieldset>
    </v-form>

    <fieldset v-if="legalSpecial" class="legalSection">
      <legend class="text-h6 text-sm-h5">Conditions et critères</legend>
      <section class="mentionLegal">
        L'autorisation spéciale temporaire <b>s'adresse aux personnes qui répondent aux conditions d'admissibilité</b>
        suivantes :
        <ol>
          <li>Être titulaire d'un baccalauréat ou d'une maîtrise en T.S. ou d'une maîtrise en T.C.F. OU</li>
          <li>
            Être légalement autorisé à exercer la profession de travailleur social ou de thérapeute conjugal et familial hors du Québec
            (titulaire d'un permis) et être en règle avec l'organisme ayant délivré l'autorisation d'exercice OU
          </li>
          <li>
            Être invité par un établissement, une université ou un autre organisme dans le but d'apprendre ou d'enseigner des activités
            réservées OU
          </li>
          <li>
            Être invité par un établissement, une université ou un autre organisme dans le but d'exercer de façon ponctuelle auprès d'une
            personne ou d'un groupe de personnes ciblées;
          </li>
          <li>Être amené à offrir temporairement des services au Québec.</li>
          <li>L'autorisation spéciale est valable pour une période d'un an ou moins (selon la durée recherchée).</li>
        </ol>
        <br />
        <p class="font-italic">
          Attention : lorsqu'un candidat est invité à venir au Québec pour donner des services à la population, la voie à favoriser est
          celle du permis d'exercice. Pour la personne légalement autorisée à exercer hors Québec et qui est par ailleurs titulaire d'un
          permis d'exercice au Québec sans être inscrit au tableau de l'Ordre,
          <b>la réinscription au tableau de l'Ordre doit être privilégiée.</b>
        </p>
        <p>
          Retrouver plus d'informations sur
          <a
            href="https://www1.otstcfq.org/exercer-au-quebec/exercer-au-quebec/admission-et-inscription/autorisation-speciale-temporaire/"
            target="_blank"
            >l'autorisation spéciale temporaire</a
          >
          sur notre site internet.
        </p>
      </section>
    </fieldset>

    <!-- Autre Autorisation de pratique -->
    <fieldset>
      <legend class="text-h6 text-sm-h5">Autre(s) autorisation(s) de pratique</legend>
      <section class="body-2">
        <p class="mentionLegal">
          <RessourceInfoBulle code="PI-AP-2" />
        </p>
      </section>

      <!-- NumeroDePermisDePsychotherapeuteSiApplicable -->
      <v-text-field
        v-model="getAutresAutorisationsDePratique.numeroDePermisDePsychotherapeuteSiApplicable"
        label="Numéro de permis de psychothérapeute, si applicable"
      ></v-text-field>

      <v-autocomplete
        v-model="getAutresAutorisationsDePratique.membreDUnAutreOrdre"
        :item-text="itemText"
        :item-value="itemValue"
        :items="listesAutresOrdres"
        clearable
        label="Membre d'un ou plusieurs ordres du Québec (n.b. si vous êtes uniquement psychothérapeute, vous n'êtes pas considéré être membre de l'Ordre des psychologues)"
        multiple
        return-object
      ></v-autocomplete>

      <!-- Citoyennete -->
      <v-autocomplete
        v-model="getAutresAutorisationsDePratique.instancesReglementairesEnTravailSocialHorsQuebec"
        :item-text="itemText"
        :item-value="itemValue"
        :items="listesInstancesReglementaires"
        clearable
        label="Instances règlementaires en Travail social hors Québec"
        multiple
        return-object
      ></v-autocomplete>
    </fieldset>

    <fieldset v-if="legalEtudiant" class="legalSection">
      <legend class="text-h6 text-sm-h5">Condition étudiant</legend>
      <p class="mentionLegal">
        Une inscription à ce registre permet l'exercice, par des étudiants inscrits aux programmes universitaires du Québec, de certaines
        activités professionnelles réservées aux membres de l'Ordre dans le cadre d'un emploi rémunéré et sous supervision.
      </p>
      <p class="mentionLegal">
        Cette autorisation temporaire prendra fin à la première occurrence, soit l'obtention du grade donnant accès au permis de l'Ordre, la
        fin du contrat de travail ou l'abandon du programme d'études.
        <br />
        Retrouver plus d'information sur
        <a
          href="https://www1.otstcfq.org/exercer-au-quebec/exercer-au-quebec/admission-et-inscription/registre-des-etudiants/"
          target="_blank"
          >le registre des étudiants</a
        >
        sur notre site internet.
      </p>
    </fieldset>

    <div class="text-center mt-6 d-flex justify-space-between">
      <v-btn color="primary" @click="back()">Retour</v-btn>
      <v-btn :disabled="!getAutorisations || getAutorisations.length == 0" color="primary" @click="saveAndContinue()"> Suivant </v-btn>
      <v-dialog v-model="dialog" max-width="350" persistent>
        <v-card>
          <v-card-title class="headline"> Attention </v-card-title>
          <v-card-text class="subtitle-1">
            Je comprends qu'en validant ce formulaire sans autorisation de pratique, je serai retiré du tableau de l'ordre.
          </v-card-text>
          <v-card-actions>
            <v-btn color="error" text @click="dialog = false"> Annuler </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="processRetraitVolontaire"> Je suis d'accord </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import Loader from "../Loader"
import NavigationPageFormulaireInscriptionMixin from "@/mixins/navigationPageFormulaireInscriptionMixin"

export default {
  mixins: [NavigationPageFormulaireInscriptionMixin],
  components: {
    Loader: Loader,
  },
  props: {
    routeName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      overlay: false,
      messageSelector: "",
      valid: true,
      rules: {
        CategorieAutorisation: [(v) => (!!v && v.length != 0) || "Le type d'autorisation est requis"],
        Autorisation: [(v) => (!!v && v.length != 0) || "L'autorisation est requise"],
      },
      dialog: false,
    }
  },
  computed: {
    ...mapGetters({
      demandeInscriptionId: "DemandeInscription/GET_DEMANDEINSCRIPTIONID",
      getAutorisations: "AutorisationDePratique/GET_AUTORISATIONSDEPRATIQUE",
      listeCategories: "Catalogue/GET_CATEGORIEAUTORISATION",
      listeFilteredAutorisations: "Catalogue/GET_AUTORISATIONS_BY_CATEGORIEAUTORISATION",
      getAutresAutorisationsDePratique: "AutresAutorisationsDePratique/GET_AUTRESAUTORISATIONDEPRATIQUES",
      listesAutresOrdres: "Catalogue/GET_AUTRESORDRE",
      listesInstancesReglementaires: "Catalogue/GET_INSTANCESREGLEMENTAIRES",
      getFormations: "FormationInitiale/GET_FORMATIONS",
    }),
    legalSpecial: function () {
      return (
        this.getAutorisations &&
        this.getAutorisations.find(
          (t) => t.categorieAutorisation && t.categorieAutorisation.id === this.$catalogues.categorieAutorisation.autorisationSpeciale
        )
      )
    },
    legalEtudiant: function () {
      return (
        this.getAutorisations &&
        this.getAutorisations.some(
          (t) => t.categorieAutorisation && t.categorieAutorisation.id === this.$catalogues.categorieAutorisation.registreDesEtudiants
        )
      )
    },
    listeAutorisationsDePratique() {
      return this.getAutorisations.filter((a) => a.statut?.id === this.$catalogues.statutAutorisationDePatique.autoriseInscrire && a.deleted !== true)
    },
    listeCategoriesAutorisationsFilter() {
      return this.listeCategories.sort((a, b) => (a.lang00 < b.lang00 ? 1 : -1))
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    ...mapActions({
      getCatalogue: "Catalogue/getCatalogue",
      updateAutorisationDePratique: "AutorisationDePratique/updateAutorisationDePratique",
      getAutorisationDePratiqueByDemandeInscriptionId: "AutorisationDePratique/getAutorisationDePratiqueByDemandeInscriptionId",
      retraitVolontaire: "DemandeInscription/retraitVolontaire",
      demandeTerminee: "DemandeInscription/demandeTerminee",
      updateAutresAutorisationsDePratique: "AutresAutorisationsDePratique/updateAutresAutorisationsDePratique",
      createAutresAutorisationsDePratique: "AutresAutorisationsDePratique/createAutresAutorisationsDePratique",
      getAutresAutorisationsDePratiqueByDemandeInscriptionId:
        "AutresAutorisationsDePratique/getAutresAutorisationsDePratiqueByDemandeInscriptionId",
    }),
    async fetchItems() {
      try {
        this.messageSelector = "Loading"
        this.overlay = true

        const requests = [
          this.getCatalogue(this.$cataloguesDesc.categorieAutorisation),
          this.getCatalogue(this.$cataloguesDesc.statutAutorisationDePatique),
          this.getCatalogue(this.$cataloguesDesc.autorisationDePatique),
          this.getCatalogue(this.$cataloguesDesc.membreAutresOrdres),
          this.getCatalogue(this.$cataloguesDesc.instancesReglementaires),
        ]

        requests.push(this.getAutorisationDePratiqueByDemandeInscriptionId(this.demandeInscriptionId))
        requests.push(this.getAutresAutorisationsDePratiqueByDemandeInscriptionId(this.demandeInscriptionId))

        await Promise.all(requests)

        this.overlay = false
      } catch (erreur) {
        console.log(erreur)
      }
    },
    saveAndContinue() {
      if (
        this.listeAutorisationsDePratique.filter((a) => a.statut && a.statut.id !== this.$catalogues.statutAutorisationDePatique.retire).length === 0 &&
        this.listeAutorisationsDePratique.filter((a) => a.statut == null || a.statut.id !== this.$catalogues.statutAutorisationDePatique.retire).length === 0
      ) {
        this.dialog = true
      } else {
        this.save()
      }
    },
    async save() {
      this.messageSelector = "Save"
      this.overlay = true

      for (const element of this.listeAutorisationsDePratique) {
        await this.updateAutorisationDePratique(element)
      }

      // Autre Autorisation de pratique
      if (this.demandeAdmissionId) {
        this.getAutresAutorisationsDePratique.demandeInscription = this.demandeInscriptionId
      }

      if (this.contactId) {
        this.getAutresAutorisationsDePratique.pp = this.contactId
      }

      if (!this.getAutresAutorisationsDePratique.id) {
        await this.createAutresAutorisationsDePratique(this.getAutresAutorisationsDePratique)
      } else {
        await this.updateAutresAutorisationsDePratique(this.getAutresAutorisationsDePratique)
      }

      this.overlay = false
      this.next()
    },
    addRow(item) {
      item.statut.id = this.$catalogues.statutAutorisationDePatique.membre
      this.updateAutorisationDePratique(item)
    },
    itemText: (item) => item.lang00,
    itemValue: (item) => item.id,
    isNotRetire(item) {
      return !(item.statut === this.$catalogues.statutAutorisationDePatique.retire && item.deleted == null)
    },
    async processRetraitVolontaire() {
      for (const element of this.listeAutorisationsDePratique) {
        await this.updateAutorisationDePratique(element)
      }
      await this.retraitVolontaire()
      await this.demandeTerminee()
      this.$router.push({
        name: `DemandeInscription/RetraitVolontaire`,
      })
    },
  },
}
</script>

<style></style>